import { useEffect, useState } from 'react'
import {
  type FieldErrors,
  type FieldValues,
  type Path,
  type PathValue,
  type RegisterOptions,
  type UseFormClearErrors,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetError,
  type UseFormSetValue,
  type UseFormTrigger,
  type UseFormWatch,
} from 'react-hook-form'
import { useDebounceValue } from 'usehooks-ts'

import { useMapboxSearch } from 'components/hooks/useMapboxSearch'
import { useMapboxSessionToken } from 'components/hooks/useMapboxSessionToken'
import { Autocomplete, InputBlock, TextButton, TextInput } from 'slab'

export interface AddressFieldsType extends FieldValues {
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zip: string;
}

interface AddressFieldsProps<T extends AddressFieldsType> {
  accessToken: string;
  disabled?: boolean;
  showTopLevelLabel?: boolean;
  getValues: UseFormGetValues<T>;
  setValue: UseFormSetValue<T>;
  register: UseFormRegister<T>;
  trigger: UseFormTrigger<T>;
  setError: UseFormSetError<T>;
  clearErrors: UseFormClearErrors<T>;
  errors: FieldErrors<T>;
  watch: UseFormWatch<T>;
  validationRules?: {
    address_line1?: RegisterOptions<T, Path<T>>;
    address_line2?: RegisterOptions<T, Path<T>>;
    city?: RegisterOptions<T, Path<T>>;
    state?: RegisterOptions<T, Path<T>>;
    zip?: RegisterOptions<T, Path<T>>;
  };
  showRequired?: boolean;
}

export function SlabAddressFields<T extends AddressFieldsType> ({
  accessToken,
  disabled,
  getValues,
  setValue,
  register,
  trigger,
  setError,
  clearErrors,
  showTopLevelLabel = true,
  errors,
  validationRules,
  watch,
  showRequired = false,
}: AddressFieldsProps<T>) {
  const [query, setQuery] = useState<string>(() => {
    const addressLine1 = getValues('address_line1' as Path<T>) ?? ''
    const addressLine2 = getValues('address_line2' as Path<T>) ?? ''
    const city = getValues('city' as Path<T>) ?? ''
    const state = getValues('state' as Path<T>) ?? ''
    const zip = getValues('zip' as Path<T>) ?? ''

    const parts = [addressLine1, addressLine2, city, state, zip].filter(
      Boolean
    )
    return parts.join(', ')
  })
  const [debouncedQuery] = useDebounceValue(query, 500)
  const [expanded, setExpanded] = useState(() => {
    const addressLine1 = getValues('address_line1' as Path<T>) ?? undefined
    const city = getValues('city' as Path<T>) ?? undefined
    const state = getValues('state' as Path<T>) ?? undefined
    const zip = getValues('zip' as Path<T>) ?? undefined

    if (addressLine1 && city && state && zip) {
      return true
    }
    return false
  })

  const [showAutocomplete, setShowAutocomplete] = useState(true)

  // Watch for changes to the address fields
  const addressLine1 = watch('address_line1' as Path<T>)
  const addressLine2 = watch('address_line2' as Path<T>)
  const city = watch('city' as Path<T>)
  const state = watch('state' as Path<T>)
  const zip = watch('zip' as Path<T>)

  // This effect ensures the component stays in sync with form values that might be updated externally
  // (e.g., from API calls) when the component is in the non-expanded state
  useEffect(() => {
    if (!expanded && addressLine1 && city && state && zip) {
      setExpanded(true)
      const parts = [addressLine1, addressLine2, city, state, zip].filter(
        Boolean
      )
      setQuery(parts.join(', '))
    }
  }, [addressLine1, addressLine2, city, state, zip, expanded])

  const sessionToken = useMapboxSessionToken()

  const { data: searchResults, isLoading } = useMapboxSearch({
    query: debouncedQuery,
    accessToken,
    sessionToken,
  })

  const handleSelect = (suggestion: any) => {
    clearErrors('address_line1' as Path<T>)
    clearErrors('address_line2' as Path<T>)
    clearErrors('city' as Path<T>)
    clearErrors('state' as Path<T>)
    clearErrors('zip' as Path<T>)

    setValue(
      'address_line1' as Path<T>,
      suggestion.context.address.name as PathValue<T, Path<T>>,
      { shouldDirty: true, shouldTouch: true }
    )
    setValue(
      'city' as Path<T>,
      suggestion.context.place.name as PathValue<T, Path<T>>,
      { shouldDirty: true, shouldTouch: true }
    )
    setValue(
      'state' as Path<T>,
      suggestion.context.region.region_code as PathValue<T, Path<T>>,
      { shouldDirty: true, shouldTouch: true }
    )
    setValue(
      'zip' as Path<T>,
      suggestion.context.postcode.name as PathValue<T, Path<T>>,
      { shouldDirty: true, shouldTouch: true }
    )

    if (!expanded) {
      setExpanded(true)
    }
  }

  const notExpandedCmp = (
    <>
      <Autocomplete>
        <Autocomplete.Input asChild>
          <TextInput
            label='Street address'
            placeholder='123 Main St'
            value={query}
            disabled={disabled}
            onChange={({ target: { value } }) => {
              setQuery(value)
            }}
            error={errors.address_line1?.message?.toString()}
          />
        </Autocomplete.Input>
        <Autocomplete.List>
          {!searchResults?.suggestions && isLoading && (
            <Autocomplete.Option disabled>Searching...</Autocomplete.Option>
          )}
          {!searchResults?.suggestions && !isLoading && (
            <Autocomplete.Option disabled>
              Type your search results..
            </Autocomplete.Option>
          )}
          {searchResults?.suggestions.length === 0 && (
            <Autocomplete.Option disabled>No matches found</Autocomplete.Option>
          )}
          {searchResults?.suggestions.map((suggestion) => (
            <Autocomplete.Option
              key={suggestion.full_address}
              value={null}
              redactedFromPosthog
              onSelect={() => handleSelect(suggestion)}
            >
              {suggestion.address} {suggestion.place_formatted}
            </Autocomplete.Option>
          ))}
        </Autocomplete.List>
      </Autocomplete>
      <div className='flex justify-between items-center'>
        {showRequired && <p className='text-xs text-gray-500'>Required</p>}
        <div className='body-sm text-gray-500 ml-auto'>
          <TextButton onClick={() => {
            setExpanded(true)
            setShowAutocomplete(false)
          }}
          >Enter address manually
          </TextButton>
        </div>
      </div>
    </>
  )

  const expandedCmpAutocomplete = (
    <Autocomplete>
      <Autocomplete.Input asChild>
        <InputBlock.Input
          disabled={disabled}
          label='Street address'
          placeholder='123 Main St'
          {...register(
            'address_line1' as Path<T>,
            validationRules?.address_line1
          )}
          error={errors.address_line1?.message?.toString()}
          onChange={({ target: { value } }) => {
            // We need to validate the query value immediately, even though it's not yet in the form.
            // This is because the query text and form value are decoupled - the form value only
            // updates when an address is selected from the dropdown. However, we still want to
            // show validation errors (like PO Box or required field) as the user types.
            if (validationRules?.address_line1?.required && !value) {
              setError('address_line1' as Path<T>, {
                type: 'manual',
                message: validationRules?.address_line1?.required as string,
              })
              return
            }
            if (validationRules?.address_line1?.validate) {
              const validateFn = validationRules.address_line1.validate as (
                value: string
              ) => boolean | string
              const validationResult = validateFn(value)

              if (typeof validationResult === 'string') {
                setError('address_line1' as Path<T>, {
                  type: 'manual',
                  message: validationResult,
                })
                return
              }
            }

            // All validation checks passed - update form state and clear any previous errors
            // This ensures the form's isValid state is updated correctly
            clearErrors('address_line1' as Path<T>)
            setValue(
              'address_line1' as Path<T>,
              value as PathValue<T, Path<T>>,
              { shouldDirty: true, shouldTouch: true }
            )
            trigger('address_line1' as Path<T>)
            setQuery(value)
          }}
        />
      </Autocomplete.Input>
      <Autocomplete.List>
        {!searchResults?.suggestions && isLoading && (
          <Autocomplete.Option disabled>Searching...</Autocomplete.Option>
        )}
        {!searchResults?.suggestions && !isLoading && (
          <Autocomplete.Option disabled>
            Type your search results..
          </Autocomplete.Option>
        )}
        {searchResults?.suggestions.length === 0 && (
          <Autocomplete.Option disabled>No matches found</Autocomplete.Option>
        )}
        {searchResults?.suggestions.map((suggestion) => (
          <Autocomplete.Option
            key={suggestion.full_address}
            value={null}
            redactedFromPosthog
            onSelect={() => handleSelect(suggestion)}
          >
            {suggestion.address} {suggestion.place_formatted}
          </Autocomplete.Option>
        ))}
      </Autocomplete.List>
    </Autocomplete>
  )

  const expandedCmp = (
    <>
      <InputBlock label={showTopLevelLabel ? 'Street address' : undefined}>
        <InputBlock.Row>
          {showAutocomplete
            ? expandedCmpAutocomplete
            : <InputBlock.Input
                label='Street address'
                {...register('address_line1' as Path<T>, validationRules?.address_line1)}
                error={errors.address_line1?.message?.toString()}
              />}
        </InputBlock.Row>
        <InputBlock.Row>
          <InputBlock.Input
            label='Address line 2'
            error={errors.address_line2?.message?.toString()}
            disabled={disabled}
            {...register(
              'address_line2' as Path<T>,
              validationRules?.address_line2
            )}
          />
        </InputBlock.Row>
        <InputBlock.Row>
          <InputBlock.Input
            label='City'
            span='3'
            error={errors.city?.message?.toString()}
            disabled={disabled}
            value={getValues('city' as Path<T>)}
            {...register('city' as Path<T>, validationRules?.city)}
          />
          <InputBlock.Select
            label='State'
            placeholder='Select one'
            span='2'
            error={errors.state?.message?.toString()}
            disabled={disabled}
            {...register('state' as Path<T>, validationRules?.state)}
          >
            <option value=''>Select a state</option>
            <option value='AL'>Alabama</option>
            <option value='AK'>Alaska</option>
            <option value='AZ'>Arizona</option>
            <option value='AR'>Arkansas</option>
            <option value='CA'>California</option>
            <option value='CO'>Colorado</option>
            <option value='CT'>Connecticut</option>
            <option value='DE'>Delaware</option>
            <option value='DC'>District Of Columbia</option>
            <option value='FL'>Florida</option>
            <option value='GA'>Georgia</option>
            <option value='HI'>Hawaii</option>
            <option value='ID'>Idaho</option>
            <option value='IL'>Illinois</option>
            <option value='IN'>Indiana</option>
            <option value='IA'>Iowa</option>
            <option value='KS'>Kansas</option>
            <option value='KY'>Kentucky</option>
            <option value='LA'>Louisiana</option>
            <option value='ME'>Maine</option>
            <option value='MD'>Maryland</option>
            <option value='MA'>Massachusetts</option>
            <option value='MI'>Michigan</option>
            <option value='MN'>Minnesota</option>
            <option value='MS'>Mississippi</option>
            <option value='MO'>Missouri</option>
            <option value='MT'>Montana</option>
            <option value='NE'>Nebraska</option>
            <option value='NV'>Nevada</option>
            <option value='NH'>New Hampshire</option>
            <option value='NJ'>New Jersey</option>
            <option value='NM'>New Mexico</option>
            <option value='NY'>New York</option>
            <option value='NC'>North Carolina</option>
            <option value='ND'>North Dakota</option>
            <option value='OH'>Ohio</option>
            <option value='OK'>Oklahoma</option>
            <option value='OR'>Oregon</option>
            <option value='PA'>Pennsylvania</option>
            <option value='RI'>Rhode Island</option>
            <option value='SC'>South Carolina</option>
            <option value='SD'>South Dakota</option>
            <option value='TN'>Tennessee</option>
            <option value='TX'>Texas</option>
            <option value='UT'>Utah</option>
            <option value='VT'>Vermont</option>
            <option value='VA'>Virginia</option>
            <option value='WA'>Washington</option>
            <option value='WV'>West Virginia</option>
            <option value='WI'>Wisconsin</option>
            <option value='WY'>Wyoming</option>
          </InputBlock.Select>
          <InputBlock.Input
            label='Zip'
            span='2'
            error={errors.zip?.message?.toString()}
            disabled={disabled}
            value={getValues('zip' as Path<T>)}
            {...register('zip' as Path<T>, validationRules?.zip)}
          />
        </InputBlock.Row>
      </InputBlock>
      {(showRequired || !showAutocomplete) && (
        <div className='flex justify-between items-center'>
          {showRequired && <p className='text-xs text-gray-500'>Required</p>}
          {!showAutocomplete && (
            <div className='body-sm text-gray-500 ml-auto'>
              <TextButton onClick={() => {
                setExpanded(false)
                setShowAutocomplete(true)
              }}
              >Use autocomplete
              </TextButton>
            </div>
          )}
        </div>
      )}
    </>
  )

  return expanded ? expandedCmp : notExpandedCmp
}
